import React from 'react';
import ViewHeader from '../../widgets/ViewHeader';
import Viewport from '../../widgets/Viewport';
import './Dashboard.scss';
import Panel from '../../widgets/Panel';
import { mergeClasses } from '../../constants';
import { Col, Row, Table } from 'reactstrap';
import { connect } from 'react-redux';
import { sessionSelector, TSecurityReadiness } from '../../reducers/session';
import Icon from '../../widgets/Icons';
import LightTooltip, {
  TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE,
} from '../../widgets/LightTooltip';
import { PageTitle } from '../../widgets/PageTitle';

type ScoreOutOfProps = {
  score: number;
  outOf: number;
  className?: string;
};

export const ScoreOutOf = ({ score, outOf, className }: ScoreOutOfProps) => {
  return (
    <div className={mergeClasses(['font-weight-bold', className || ''])}>
      <span className="score-value" style={{ fontSize: 'x-large' }}>
        {score}
      </span>
      <span style={{ padding: 10 }}>/</span>
      <span data-cy="out-of-value">{outOf}</span>
    </div>
  );
};

type LabeledScoreProps = {
  label: string;
  score: number | string | boolean;
};

export const LabeledScore = ({ label, score }: LabeledScoreProps) => {
  if (typeof score === 'boolean') {
    score = score ? 'Y' : 'N';
  }
  return (
    <div className="d-flex pr-lg-5">
      <span>{label}</span>
      <span className="score-value ml-auto">{score}</span>
    </div>
  );
};

const ToolTipIcon = ({ content }: { content: React.ReactNode }) => {
  return (
    <LightTooltip
      size="lg"
      tooltip={content}
      delay={TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE}
      placement="top"
    >
      <Icon.Question size={14} className="ml-1 text-muted" />
    </LightTooltip>
  );
};

export type StoreProps = {
  securityReadiness: TSecurityReadiness | null;
};

const SecurityReadiness = ({ securityReadiness }: StoreProps) => {
  if (!securityReadiness) {
    return (
      <Viewport>
        <PageTitle title="Coverage Score" />
        <ViewHeader title="Coverage Score" />
        <Panel>
          <p>
            Your coverage score is not available yet. Please contact your CST
            for more information.
          </p>
        </Panel>
      </Viewport>
    );
  }

  const { scores, stats } = securityReadiness;

  let industryAvg;
  if (stats.industry) {
    industryAvg = (
      <div className="mx-lg-5 mx-3" data-cy="industry-avg">
        <h5 className="mb-2">Industry</h5>
        <p className="score-value">
          {stats.industry.toFixed(1)}
          {` (${scores.industry})`}
        </p>
      </div>
    );
  }

  let sizeAvg;
  if (stats.size) {
    const { num_employees_lt, num_employees_gte } = stats.size;
    const sizeRange = num_employees_lt
      ? `<${num_employees_lt}`
      : `${num_employees_gte}+`;
    sizeAvg = (
      <div className="ml-lg-5 ml-3" data-cy="size-avg">
        <h5 className="mb-2">
          <span>Size</span>
          <ToolTipIcon
            content={
              <div>
                Compared to clients of similar size
                <br />
                Size is banded into:
                <br />
                &lt;250, &lt;500, &lt;750, &lt;1000,
                <br />
                &lt;1500, &lt;3000 and 3000+
              </div>
            }
          />
        </h5>
        <p className="score-value" data-cy="company-size">
          {stats.size.value.toFixed(1)}
          {` (${sizeRange})`}
        </p>
      </div>
    );
  }

  let cloudAppScore = 'N/A';
  if (
    scores.obs_exist_for_configured_cloud_apps &&
    scores.num_cloud_apps_detected &&
    scores.num_cloud_apps_configured
  ) {
    cloudAppScore = `${scores.num_cloud_apps_configured} / ${scores.num_cloud_apps_detected}`;
  }

  const ticketScore =
    typeof scores.num_monthly_incidents !== 'number'
      ? scores.num_monthly_incidents
      : scores.num_monthly_incidents <= 15
      ? 'Low'
      : scores.num_monthly_incidents <= 25
      ? 'Medium'
      : 'High';

  const snmpScore =
    typeof scores.has_recent_snmp_observations === 'number'
      ? scores.has_recent_snmp_observations > 0 // convert to boolean for display
      : scores.has_recent_snmp_observations; // allow override e.g. "N/A"

  const nMapScanScore =
    typeof scores.num_internal_nmap_scanned_in_past_week === 'number'
      ? scores.num_internal_nmap_scanned_in_past_week > 0
      : scores.num_internal_nmap_scanned_in_past_week;

  const scanTypesScore =
    typeof scores.num_scan_types_configured === 'number'
      ? scores.num_scan_types_configured > 0
      : scores.num_scan_types_configured;

  return (
    <Viewport>
      <PageTitle title="Coverage Score" />
      <ViewHeader title="Coverage Score" />
      <Panel>
        <Row>
          <Col md={6}>
            <h4>Coverage Score</h4>
            <ScoreOutOf
              score={scores.security_score}
              outOf={100}
              className="my-4"
            />
            <div className="d-flex my-4 all-customer-stat">
              <div className="mr-lg-5 mr-3">
                <h5 className="mb-2">All Customers</h5>
                <p className="score-value">{stats.avg.toFixed(1)}</p>
              </div>
              {industryAvg}
              {sizeAvg}
            </div>
          </Col>
          <Col md={6}>
            <h4>Coverage Score Explanation</h4>
            <p className="mt-4">
              Your Coverage Score represents key security metrics as well as how
              well your environment is engaged with the Arctic Wolf Managed
              Detection and Response (MDR) services.
            </p>
            <p className="mb-0">
              The Coverage Score is comprised of five components: Configuration,
              Monitoring, Incidents, Ticketing, and External Scans. See below
              for additional information about these components and why they
              play a part in your Coverage Score.
            </p>
          </Col>
        </Row>
      </Panel>

      <Panel>
        <h4>Coverage Score Breakdown</h4>
        <Table className="coverage-score-breakdown mt-4">
          <thead>
            <tr>
              <th>
                Configuration
                <ToolTipIcon
                  content={
                    <div>
                      Proper configuration is required for accurate alerting and
                      detection.
                      <br />
                      The components below are core to the Arctic Wolf MDR
                      service and are necessary to provide the Analysts and
                      Engineers watching over your environment the context and
                      data needed to determine the health and state of your
                      environment.
                    </div>
                  }
                />
              </th>
              <th>
                Monitoring
                <ToolTipIcon
                  content={
                    <div>
                      The monitoring component ensures that certain measures are
                      taken place to protect your environment.
                      <br />
                      This includes anti-virus running at both the server and
                      desktop level as well as monitoring your cloud services.
                    </div>
                  }
                />
              </th>
              <th>
                Incidents
                <ToolTipIcon
                  content={
                    <div>
                      This is a measure of the amount of security incidents
                      occurring within your environment. Your coverage score
                      will begin to drop if your environment hits certain
                      thresholds.
                      <br />
                      This metric can also be a good indicator of a systemic or
                      even operational issue that could be cause for concern.
                    </div>
                  }
                />
              </th>
              <th>
                Ticketing
                <ToolTipIcon
                  content={
                    <div>
                      Key components of a good security and incident response
                      plan are timing and communication.
                      <br />
                      The ticketing portion of your score reflects the number of
                      configured escalation paths. This portion of the score
                      helps Arctic Wolf ensure that clear escalation paths are
                      defined so that we can properly engage as part of your
                      team.
                    </div>
                  }
                />
              </th>
              <th>
                External Scans
                <ToolTipIcon
                  content={
                    <div>
                      Addressing external vulnerabilities is critical to your
                      security and lowering risk of compromise.
                      <br />
                      Your coverage score measures outstanding high and critical
                      external vulnerabilities as a key metric.
                    </div>
                  }
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ScoreOutOf score={scores.configuration_score} outOf={35} />
              </td>
              <td>
                <ScoreOutOf score={scores.monitoring_score} outOf={15} />
              </td>
              <td>
                <ScoreOutOf score={scores.incidents_score} outOf={20} />
              </td>
              <td>
                <ScoreOutOf score={scores.ticketing_score} outOf={15} />
              </td>
              <td>
                <ScoreOutOf score={scores.exscan_score} outOf={15} />
              </td>
            </tr>
            <tr>
              <td>
                <LabeledScore label="Device Identity" score={nMapScanScore} />
              </td>
              <td>
                <LabeledScore label="Cloud Apps" score={cloudAppScore} />
              </td>
              <td>
                <LabeledScore label="Incident Count" score={ticketScore} />
              </td>
              <td>
                <LabeledScore
                  label="Escalations"
                  score={scores.num_escalations_configured}
                />
              </td>
              <td>
                <LabeledScore
                  label="Critical"
                  score={scores.num_critical_vulnerabilities}
                />
              </td>
            </tr>
            <tr>
              <td>
                <LabeledScore
                  label="ExScan Configuration"
                  score={scanTypesScore}
                />
              </td>
              <td>
                <LabeledScore
                  label="Server AV"
                  score={scores.server_av_found}
                />
              </td>
              <td />
              <td />
              <td>
                <LabeledScore
                  label="High"
                  score={scores.num_high_vulnerabilities}
                />
              </td>
            </tr>
            <tr>
              <td>
                <LabeledScore label="SNMP" score={snmpScore} />
              </td>
              <td>
                <LabeledScore
                  label="Desktop AV"
                  score={scores.desktop_av_found}
                />
              </td>
              <td />
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <LabeledScore
                  label="AD Logs"
                  score={scores.ad_nxlog_parser_exists}
                />
              </td>
              <td />
              <td />
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <LabeledScore
                  label="FW Logs"
                  score={scores.fw_log_parser_exists}
                />
              </td>
              <td />
              <td />
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <LabeledScore
                  label="GeoIP Restrictions"
                  score={scores.customer_in_geo_approved_or_restricted_list}
                />
              </td>
              <td />
              <td />
              <td />
              <td />
            </tr>
            <tr>
              <td>
                <LabeledScore
                  label="User Inventory"
                  score={scores.ad_poller_logs_exist}
                />
              </td>
              <td />
              <td />
              <td />
              <td />
            </tr>
          </tbody>
        </Table>
      </Panel>
    </Viewport>
  );
};

export default connect<StoreProps, {}, {}>(
  (store): StoreProps => ({
    securityReadiness: sessionSelector.getSecurityReadiness(store),
  }),
  {},
)(SecurityReadiness);
